import { Link, useNavigate, useParams } from "react-router-dom";
import { useRecord } from "../api/useRecord";
import { Box, Button, Divider, IconButton, LinearProgress, Paper } from "@mui/material";
import { Process } from "./types";
import { Typography } from "@mui/material";
import { ProcessResponsibleUsers } from "./components/ProcessResponsibleUsers";
import { AccountTree, Delete } from "@mui/icons-material";
import { ProcessesBreadcrumbs } from "./components/ProcessesBreadcrumbs";
import { applyResponsiveImageStyles, checkParentCategoriesUnderRoleSupervision, checkSpecialUsersCanEditParentCategories, extractUsersFromRoles, processUpdatedAt } from "./utils";
import { ROLES } from "../utils/constants";
import { useUser } from "../api/useAuth";
import { useDeleteRecord } from "../api/useDeleteRecord";
import { useStore } from "../store/store";
import { useEffect } from "react";
import { Alerts } from "../store/store.types";

export const ProcessDetail = () => {
    const { parentcategoryid, processid, categoryid } = useParams();
    const categoryUrl = `/process_category/${categoryid}/processes`;

    const entity = "processes";

    const { data, status } = useRecord(
        "processes",
        "*.*,responsible_role.users.*, supervisores_role.directus_roles_id.users.*, responsibles_role.directus_roles_id.users.*, process_category.process_parent_category.*",
        processid
    );
    const mutation = useDeleteRecord();
    const { alerts } = useStore();
    const navigate = useNavigate();

    useEffect(() => {
        if (alerts.allowExecution && entity && processid && categoryid) {
            mutation.mutate({
                entity: entity,
                id: Number(processid),
                doStuffAfterDelete: () => doStuffAfterDelete(navigate, categoryUrl, alerts),
            });

            alerts.setAllowExecution(false);
        }
    }, [alerts, alerts.allowExecution, mutation, navigate]);

    const { data: currentUser } = useUser();
    const process = data?.item as Process;

    if (status === "loading") return <LinearProgress color="secondary" />;
    if (status === "error") return <div>Error</div>;

    const responsiveContent = applyResponsiveImageStyles(process.content);

    const doStuffAfterDelete = (navigate: any, url: string, alerts: Alerts) => {
        navigate(categoryUrl);
        alerts.setIsOpen(false);
        return;
    };

    const responsibleUsers = extractUsersFromRoles(process.responsibles_role);
    const supervisorUsers = extractUsersFromRoles(process.supervisores_role);
    const isAdmin =
        currentUser?.role.id === ROLES.admin ||
        currentUser?.role.id === ROLES.shilan ||
        currentUser?.role.id === ROLES.human_resources_manager;
    const isProcessCreatedByUser = process.user_created.id === currentUser?.id;
    const isSupervisor = supervisorUsers.some((user) => user.id === currentUser?.id);
    const isProcessUnderRoleSupervision = checkParentCategoriesUnderRoleSupervision(currentUser?.role.id, Number(parentcategoryid));
    const canUserEditParentCategory = checkSpecialUsersCanEditParentCategories(currentUser?.id, Number(parentcategoryid));

    const canUpdate = isAdmin || isProcessCreatedByUser || isSupervisor || isProcessUnderRoleSupervision || canUserEditParentCategory

    return (
        <Paper sx={{ p: 8, flex: 1 }}>
            <Box sx={{ maxWidth: "1200px", mx: "auto", gap: 4 }}>
                <ProcessesBreadcrumbs process={process} />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="h4" color="secondary">
                        {process.title}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 1,
                        }}
                    >
                        <AccountTree fontSize="large" color="secondary" />
                        <Typography variant="caption">Atualizado: {processUpdatedAt(process)}</Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <ProcessResponsibleUsers title="Responsáveis" responsibleUsers={responsibleUsers} />
                    {canUpdate && (
                        <Box sx={{ display: "flex", gap: 4 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                component={Link}
                                to={`/processes/${processid}/update`}
                            >
                                Editar
                            </Button>
                            <IconButton
                                onClick={() => {
                                    alerts.setIsOpen(true);
                                }}
                                size="large"
                                color="error"
                            >
                                <Delete />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                <ProcessResponsibleUsers title="Supervisor" responsibleUsers={supervisorUsers} />
                <Box sx={{ mt: 4 }}>
                    <Divider sx={{ borderColor: "secondary.main", borderWidth: 1 }} />
                    <Box dangerouslySetInnerHTML={{ __html: responsiveContent }} />
                </Box>
            </Box>
        </Paper>
    );
};
